import { useContext, useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router";
import { DialogContent, DialogTitle, Grid, Typography } from "@mui/material";
import { useTranslation } from "react-i18next";
import axios from "axios";
import { toast } from "react-toastify";
import { navigateToLoginPage, numberWithCommas } from "../../../../utils/utility";
import useUserInfo from "../../../../hooks/useUserInfo";
import BASE_URL from "../../../../constants/baseUrls";
import { LocaleContext } from "../../../../routing/LangRouter";
import { Add, AddCircle, InfoCircle } from "iconsax-react";
import useDebouncedInputChange from "../../../../hooks/useDebouncedInputChange ";

//import general components
import { BaseButton } from "../../../common/base-button/base-button";
import BaseTextField from "../../../common/base-text-field/base-text-field";
import BaseDialog from "../../../common/base-dialog/base-dialog";
import BaseTitle from "../../../common/base-title/base-title";
import Loading from "../../../common/loading/loading";
import BasePagination from "../../../common/base-pagination/base-pagination";
import EmptyOrErrorState from "../../../common/empty-or-error-state/empty-or-error-state";

//import view components
import DepositIncrement from "../wallet/deposit-increment";
import SmartReservationCard from "./smart-reservation-card";

export default function AutoBooking() {
    const navigate = useNavigate();
    const location = useLocation();
    const { locale } = useContext(LocaleContext);
    const { t } = useTranslation();
    const accessToken = localStorage.getItem("accessToken");

    //request to get user information
    const { userInformation, getUserInfo } = useUserInfo();
    useEffect(() => {
        getUserInfo();
    }, []);

    const [incrementDialogOpen, setIncrementDialogOpen] = useState(false);
    const handleIncrementDialog = () => {
        setIncrementDialogOpen(!incrementDialogOpen);
    };

    const [autoBookingList, setAutoBookingList] = useState(null);
    const [isLoading, setIsLoading] = useState(false);

    const [searchValue, setSearchValue] = useState("");
    // Handle search value change
    const handleSearchChange = event => {
        const { value } = event.target;
        setSearchValue(value);
        setPage(1);
        debouncedOnChange(value);
    };
    const debouncedOnChange = useDebouncedInputChange(input => {
        getAutoBookings(input, 1);
    }, 1000);

    const getAutoBookings = async (search, page) => {
        try {
            setIsLoading(true);
            const response = await axios.get(`${BASE_URL}/notification/reserve/manage/`, {
                params: {
                    search,
                    page,
                },
                headers: {
                    Authorization: `Bearer ${accessToken}`,
                },
            });
            setAutoBookingList(response.data.data);
        } catch (error) {
            if (error.response.status === 401) {
                localStorage.clear();
                navigateToLoginPage(navigate, locale, location);
            } else {
                toast.error(t("Toast Messages.Error Occurred"), { toastId: "custom-id-yes" });
            }
        } finally {
            setIsLoading(false);
        }
    };

    const [page, setPage] = useState(autoBookingList?.current_page ?? 1);
    const handlePaginationChange = async (event, value) => {
        setPage(value);
        getAutoBookings(searchValue, value);
    };

    useEffect(() => {
        getAutoBookings(searchValue, page);
    }, []);

    if (isLoading) return <Loading />;

    return (
        <Grid container item xs={12} rowGap={2}>
            <Grid className="smart-reservation__balance" container item xs={12} justifyContent="space-between" alignItems="center">
                <Grid container item xs={6} flexDirection="column">
                    <span className="wallet-dashboard__account-balance">{t("balance")}</span>
                    <span className="wallet-dashboard__amount">
                        {numberWithCommas(userInformation?.balance)} {t("currency")}
                    </span>
                </Grid>

                <Grid item xs={2}>
                    <BaseButton
                        text={t("depositIncrement")}
                        handleClick={handleIncrementDialog}
                        color="primary"
                        rightIcon={<AddCircle color="white" />}
                        size="large"
                        radius="radius-20"
                    />
                </Grid>
            </Grid>

            <BaseTitle title={t("booking")} dividerColor="1px solid var(--medium-gray-color)" />
            <Grid container item md={4}>
                <BaseTextField label={t("search")} size="medium" radius="10px" value={searchValue} setValue={e => handleSearchChange(e)} />
            </Grid>
            <Grid container item xs={12} rowGap={2} justifyContent="center">
                {autoBookingList?.results?.length ? (
                    <>
                        {autoBookingList?.results?.map(item => {
                            return (
                                <SmartReservationCard
                                    key={item?.id}
                                    item={item}
                                    type="autoBooking"
                                    fetchData={getAutoBookings}
                                />
                            );
                        })}
                        <BasePagination
                            count={autoBookingList?.total_pages}
                            page={autoBookingList?.current_page ?? 1}
                            handleChange={handlePaginationChange}
                        />
                    </>
                ) : (
                    <EmptyOrErrorState
                        title={t("Messages.No Items to Display")}
                        icon={<InfoCircle size="32" color="var(--secondary-color)" variant="Bold" />}
                    />
                )}
            </Grid>

            <BaseDialog open={incrementDialogOpen} handleClose={handleIncrementDialog} top="45%" icon="close">
                <DialogTitle display="flex" justifyContent="space-between" alignItems="center">
                    <Typography variant="span" fontSize="1.4rem" fontWeight="bold">
                        {t("depositIncrement")}
                    </Typography>
                    <Add className="close" size="32" color="var(--teal-color-18)" onClick={handleIncrementDialog} cursor="pointer" />
                </DialogTitle>
                <DialogContent dividers={true}>
                    <DepositIncrement />
                </DialogContent>
            </BaseDialog>
        </Grid>
    );
}
